// Status `positive` uses indigo (not emerald) because Quoted / Bound /
// Active are in-flight insurance states; emerald reads as
// "approved / closed".
import { Check, FileText, X } from "lucide-react";
import { type MouseEvent, type ReactNode } from "react";

import { useCitationResolution } from "@/components/assistant-ui/citation-resolution";
import { EditorialInlineText } from "@/components/assistant-ui/editorial-inline-text";
import { useMarkdownActions } from "@/components/assistant-ui/markdown-actions";
import { Badge } from "@/components/ui/badge";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
    type CellSegment,
    segmentSourceCell,
} from "./utils/cell-source-segments";
import {
    inferStatusVariant,
    isStatusVariant,
    parseBoolText,
    parseCurrencyValue,
    type StatusVariant,
} from "./utils/cell-detection";
import type { CellSpec } from "./utils/parse-markdown-table";

const STATUS_STYLES: Record<StatusVariant, { badge: string; dot: string }> = {
    positive: {
        badge: "bg-indigo-50 text-indigo-900 dark:bg-indigo-500/15 dark:text-indigo-100",
        dot: "bg-indigo-500",
    },
    warning: {
        badge: "bg-amber-50 text-amber-900 dark:bg-amber-500/15 dark:text-amber-100",
        dot: "bg-amber-500",
    },
    negative: {
        badge: "bg-rose-50 text-rose-900 dark:bg-rose-500/15 dark:text-rose-100",
        dot: "bg-rose-500",
    },
    terminal: {
        badge: "bg-slate-100 text-slate-900 dark:bg-slate-500/15 dark:text-slate-200",
        dot: "bg-slate-400",
    },
};

function resolveBool(cell: CellSpec): boolean | null {
    if (cell.tag?.kind === "bool") return cell.tag.value;
    return parseBoolText(cell.text);
}

export function CurrencyCell({ cell }: { cell: CellSpec }) {
    // `parseCurrencyValue` is the single source of truth for "is this
    // zero?"; string equality (`text === "$0"`) would miss `$0.00`,
    // `(0.00)`, etc. Unparseable strings preserve the bold so a
    // malformed cell doesn't visually disappear.
    const isZero = parseCurrencyValue(cell.text) === 0;
    return (
        <span
            className={cn(
                "block text-right tabular-nums",
                isZero ? "text-muted-foreground font-normal" : "font-semibold"
            )}
        >
            {cell.text}
        </span>
    );
}

export function EmDashCell() {
    return <span className="text-muted-foreground">—</span>;
}

export function StatusBadgeCell({ cell }: { cell: CellSpec }) {
    if (!cell.text) return null;

    const taggedVariant =
        cell.tag?.kind === "status" && isStatusVariant(cell.tag.variant)
            ? cell.tag.variant
            : null;
    const variant = taggedVariant ?? inferStatusVariant(cell.text);

    if (!variant) return <EditorialInlineText text={cell.text} />;

    const style = STATUS_STYLES[variant];
    return (
        <Badge
            variant="outline"
            className={cn(
                "gap-1.5 border-transparent tabular-nums",
                style.badge
            )}
        >
            <span
                aria-hidden="true"
                className={cn(
                    "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                    style.dot
                )}
            />
            <span>
                <EditorialInlineText text={cell.text} />
            </span>
        </Badge>
    );
}

export function BooleanIconCell({ cell }: { cell: CellSpec }) {
    const value = resolveBool(cell);
    if (value === null) return <DefaultCell cell={cell} />;
    return value ? (
        <Check
            className="h-4 w-4 text-emerald-600"
            aria-label="yes"
            role="img"
        />
    ) : (
        <X className="h-4 w-4 text-rose-600" aria-label="no" role="img" />
    );
}

// Sorting is disabled at the column-def level because the displayed
// number is the sort-stable view-index, not a cell value.
export function RowCounterCell({ rowIndex }: { rowIndex: number }) {
    return (
        <span className="text-muted-foreground bg-background inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs tabular-nums">
            {rowIndex + 1}
        </span>
    );
}

// Renders each segment of a source-bearing cell. `withLabels` controls
// whether chips echo their raw link token (mixed-content prose, e.g.
// `[7]`) or fall back to `p. {page}` (pure citation lists).
function renderSourceSegments(
    segments: CellSegment[],
    withLabels: boolean
): ReactNode {
    return segments.map((seg, i) =>
        seg.kind === "text" ? (
            <EditorialInlineText key={i} text={seg.text} />
        ) : (
            <InlineSourceChip
                key={i}
                documentId={seg.source.document_id}
                page={seg.source.page}
                handle={seg.source.handle}
                label={withLabels ? seg.source.linkLabel : undefined}
            />
        )
    );
}

// Pure citation cells (one or a comma-separated list of source links).
// Each link renders as a `p. {page}` chip with the separators preserved
// between them.
export function SourceChipCell({ cell }: { cell: CellSpec }) {
    if (!cell.sources?.length) return <span>{cell.text}</span>;
    const { segments } = segmentSourceCell(cell);
    return <>{renderSourceSegments(segments, false)}</>;
}

// Inline citation chip. Two modes share one quiet, repeated surface:
//   - legacy `link://source` cells pass `{ documentId, page, label }`;
//   - native `cite://cN` cells pass `{ handle }` and resolve page / bbox /
//     snippet from the per-message CitationResolutionProvider.
// `label`: text to render inside the chip (mixed-content cells preserve the
// original link token like `[7]`); omitted, it falls back to `p. {page}` or
// `Source`. The chip is indigo `primary` (not copper) because it is a
// high-frequency repeated surface, and the verified `Check` marker shows ONLY
// when the resolution carries a bounding box — every cited handle resolves, so
// resolution alone is not signal; a box means "pinpointed on the page".
export function InlineSourceChip({
    documentId,
    page,
    label,
    handle,
}: {
    documentId?: number;
    page?: number;
    label?: string;
    handle?: string;
}) {
    const { onSource } = useMarkdownActions();
    const resolution = useCitationResolution(handle ?? "");

    // A handle that resolves to nothing is a dead reference (reload race or an
    // unknown handle). Render an inert, muted chip rather than invent a page.
    const isDeadHandle = !!handle && !resolution;

    const resolvedDocId = handle ? resolution?.document_id : documentId;
    const resolvedPage = handle ? (resolution?.page ?? undefined) : page;
    const snippet = handle ? resolution?.snippet : undefined;
    const hasBox = !!resolution?.bbox?.length;

    const text =
        label ?? (resolvedPage != null ? `p. ${resolvedPage}` : "Source");

    if (isDeadHandle) {
        return (
            <span className="border-border bg-muted/40 text-muted-foreground inline-flex cursor-default items-center gap-1 rounded-full border px-2 py-0.5 align-baseline text-xs">
                <FileText className="h-3 w-3" aria-hidden="true" />
                <span className="tabular-nums">{label ?? "Source"}</span>
            </span>
        );
    }

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (resolvedDocId != null)
            onSource(resolvedDocId, resolvedPage, handle);
    };

    const accessibleName =
        resolvedPage != null
            ? `Open source · page ${resolvedPage}`
            : "Open source";

    // Color-only hover (no transform: a baseline shift would reflow dense
    // prose); reduced-motion collapses the transition to instant.
    const chip = (
        <button
            type="button"
            onClick={handleClick}
            title={snippet}
            aria-label={accessibleName}
            className="text-primary border-primary/20 bg-primary/10 hover:bg-primary/20 focus-visible:outline-primary/40 active:bg-primary/25 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 align-baseline text-xs transition-colors focus-visible:outline focus-visible:outline-2 motion-reduce:transition-none"
        >
            <FileText className="h-3 w-3" aria-hidden="true" />
            <span className="tabular-nums">{text}</span>
            {hasBox ? (
                <Check
                    className="h-3 w-3"
                    aria-label="located on page"
                    role="img"
                />
            ) : null}
        </button>
    );

    // Snippet preview: the impeccable micro-moment that lets a broker verify
    // the quoted figure without leaving the answer. `title` stays as the no-JS
    // / accessible fallback; the rich Tooltip is progressive enhancement.
    if (!snippet) return chip;

    return (
        <Tooltip>
            <TooltipTrigger render={chip} />
            <TooltipContent variant="rich" className="max-w-[40ch]">
                <span className="text-foreground line-clamp-3">
                    &ldquo;{snippet}&rdquo;
                </span>
                {resolvedPage != null ? (
                    <span className="text-muted-foreground text-xs">
                        Page {resolvedPage}
                    </span>
                ) : null}
            </TooltipContent>
        </Tooltip>
    );
}

// Mixed-content cells: replace each link's rendered token (`linkLabel`)
// with an inline chip while preserving surrounding prose. Falls back to
// plain text (with currency-token bolding) when there's no source link
// or no label is a substring of the cell text.
export function DefaultCell({ cell }: { cell: CellSpec }) {
    if (!cell.sources?.length) {
        return <EditorialInlineText text={cell.text} />;
    }
    const { segments } = segmentSourceCell(cell);
    return <span>{renderSourceSegments(segments, true)}</span>;
}
